import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, SectionHeader } from 'web-library';

import { makeStyles } from 'tss-react/mui';
import { ConfigItem, ControlType } from './ConfigItem';
import { configNameDic } from '../../../models';

const useStyles = makeStyles()((theme) => ({
  container: {
    marginBottom: theme.spacing(-3),
  },
}));

interface DisplaySettings {
  display: boolean;
  value: string;
}

interface SystemSettingsDetailsProps {
  systemSettingsData: { key: string; value: string }[];
}

const SystemSettingsDetails = ({ systemSettingsData }: SystemSettingsDetailsProps) => {
  const { t } = useTranslation();

  const { classes } = useStyles();

  const configNameDescriptionList = [
    configNameDic.daysToDisableAccount,
    configNameDic.daysToLockInactivityAccount,
    configNameDic.daysToEmailAdmin,
    configNameDic.daysToInvalidateLink,
    configNameDic.timesOfMaxLogin,
    configNameDic.minutesOfSessionTimeout,
    configNameDic.minutesOfSessionTimeoutPrivilegedAdmin,
    configNameDic.minutesOfSessionTimeoutNonPriviledgeAdmin,
    configNameDic.daysOfPaymentLinkExpire,
    configNameDic.daysOfAccessLinkExpire,
    configNameDic.timesOfMaxFailedAttempt,
    configNameDic.secondToWaitLogin,
  ];

  const defaultDisplaySettingsProperties = { display: false, value: '' };

  const displaySystemSettings: DisplaySettings[] = [
    defaultDisplaySettingsProperties,
    defaultDisplaySettingsProperties,
    defaultDisplaySettingsProperties,
    defaultDisplaySettingsProperties,
    defaultDisplaySettingsProperties,
    defaultDisplaySettingsProperties,
    defaultDisplaySettingsProperties,
    defaultDisplaySettingsProperties,
    defaultDisplaySettingsProperties,
    defaultDisplaySettingsProperties,
    defaultDisplaySettingsProperties,
    defaultDisplaySettingsProperties,
  ];

  systemSettingsData.forEach(({ key, value }) => {
    const currentIndex = configNameDescriptionList.findIndex((element) => key === element);
    displaySystemSettings[currentIndex] = { display: true, value };
  });

  return (
    <Box className={classes.container}>
      <SectionHeader title={t('admin->>AdminConfiguration.WebApp Settings')} />
      {displaySystemSettings[0].display && (
        <ConfigItem
          label={t('admin->>AdminConfiguration.Disable account')}
          type={ControlType.number}
          description={t(
            'admin->>AdminConfiguration.Disable the account x days after Results Release'
          )}
          defaultValue={displaySystemSettings[0].value}
          disabled
          name="daysToDisableAccount"
        />
      )}
      {displaySystemSettings[1].display && (
        <ConfigItem
          label={t('admin->>AdminConfiguration.Invalidate account')}
          type={ControlType.number}
          description={t('admin->>AdminConfiguration.InvalidateAccountAfterXDays')}
          defaultValue={displaySystemSettings[1].value}
          disabled
          name="daysToLockInactivityAccount"
        />
      )}
      {displaySystemSettings[2].display && (
        <ConfigItem
          label={t('admin->>AdminConfiguration.Delete account reminder')}
          type={ControlType.number}
          description={t(
            'admin->>AdminConfiguration.To email Admin x days after accounts disabled for Admin to view the accounts to be deleted'
          )}
          defaultValue={displaySystemSettings[2].value}
          disabled
          name="daysToEmailAdmin"
        />
      )}
      {displaySystemSettings[3].display && (
        <ConfigItem
          label={t('admin->>AdminConfiguration.Result release link expiry')}
          type={ControlType.number}
          description={t(
            'admin->>AdminConfiguration.x days for Results Release Unique Link before expiry after generating it for each candidate'
          )}
          defaultValue={displaySystemSettings[3].value}
          disabled
          name="daysToInvalidateLink"
        />
      )}
      {displaySystemSettings[4].display && (
        <ConfigItem
          label={t('admin->>AdminConfiguration.Login attempt numbers')}
          type={ControlType.number}
          description={t('admin->>AdminConfiguration.Max number of login attempts')}
          defaultValue={displaySystemSettings[4].value}
          disabled
          name="timesOfMaxLogin"
        />
      )}
      {displaySystemSettings[5].display && (
        <ConfigItem
          label={t('admin->>AdminConfiguration.Candidates and Coordinators accounts')}
          type={ControlType.number}
          description={t(
            'admin->>AdminConfiguration.x minutes of inactivity before session timeout'
          )}
          defaultValue={displaySystemSettings[5].value}
          disabled
          name="minutesOfSessionTimeout"
        />
      )}
      {displaySystemSettings[6].display && (
        <ConfigItem
          label={t('admin->>AdminConfiguration.Privileged admin accounts')}
          type={ControlType.number}
          description={t(
            'admin->>AdminConfiguration.x minutes of inactivity before session timeout'
          )}
          defaultValue={displaySystemSettings[6].value}
          disabled
          name="minutesOfSessionTimeoutPrivilegedAdmin"
        />
      )}
      {displaySystemSettings[7].display && (
        <ConfigItem
          label={t('admin->>AdminConfiguration.Non-Privileged admin accounts')}
          type={ControlType.number}
          description={t(
            'admin->>AdminConfiguration.x minutes of inactivity before session timeout'
          )}
          defaultValue={displaySystemSettings[7].value}
          disabled
          name="minutesOfSessionTimeoutNonPriviledgeAdmin"
        />
      )}
      {displaySystemSettings[8].display && (
        <ConfigItem
          label={t('admin->>AdminConfiguration.Expire payment link')}
          type={ControlType.number}
          description={t('admin->>AdminConfiguration.x days before payment unique link expires')}
          defaultValue={displaySystemSettings[8].value}
          disabled
          name="daysOfPaymentLinkExpire"
        />
      )}
      {displaySystemSettings[9].display && (
        <ConfigItem
          label={t('admin->>AdminConfiguration.Expire access link')}
          type={ControlType.number}
          description={t(
            'admin->>AdminConfiguration.x days before Exam Registration/Mother Tongue Resit/Appeal link expiry sent by Ops Admin'
          )}
          defaultValue={displaySystemSettings[9].value}
          disabled
          name="daysOfAccessLinkExpire"
        />
      )}
      {displaySystemSettings[10].display && (
        <ConfigItem
          label={t('admin->>AdminConfiguration.Failed attempt numbers')}
          type={ControlType.number}
          description={t(
            'admin->>AdminConfiguration.Max number of failed attempts to enable CAPTCHA'
          )}
          defaultValue={displaySystemSettings[10].value}
          disabled
          name="timesOfMaxFailedAttempt"
        />
      )}
      {displaySystemSettings[11].display && (
        <ConfigItem
          label={t('admin->>AdminConfiguration.Login retry seconds')}
          type={ControlType.number}
          description={t('admin->>AdminConfiguration.Max seconds (randomised) to retry CAPTCHA')}
          defaultValue={displaySystemSettings[11].value}
          disabled
          name="secondToWaitLogin"
        />
      )}
    </Box>
  );
};

export default SystemSettingsDetails;
